import { useSortable } from '@dnd-kit/sortable';
import type {
  ColumnPinningState,
  Column,
  TableFeatures,
  RowData,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { memo, useRef } from 'react';

import type { ColumnListItem } from './column-settings-types.js';
import { ColumnOrderDragHandle } from './ColumnOrderSettings/ColumnOrderDragHandle.js';
import { ColumnPinningMenu } from './ColumnPinningMenu.js';
import { ColumnVisibilityCheckbox } from './ColumnVisibilityCheckbox.js';
import * as styles from './DataTableColumnsListItem.sty.js';
import { useMergeRefs } from '../../../../../core/hooks/useMergeRefs.js';
import { Flex } from '../../../../../layouts/flex/Flex.js';
import { getColumnLabel } from '../../../../utils/get-column-label.js';

interface DataTableColumnsListItemProps<
  TFeatures extends TableFeatures,
  TData extends RowData,
> {
  /**
   * The column to render in the list.
   */
  column: ColumnListItem<TFeatures, TData>;
  /**
   * The index of the column in the list.
   */
  columnIndex: number;
  /**
   * The ordered flat columns, grouped by pinning position.
   */
  orderedFlatColumns: {
    left: ColumnListItem<TFeatures, TData>[];
    center: ColumnListItem<TFeatures, TData>[];
    right: ColumnListItem<TFeatures, TData>[];
  };
  /**
   * Whether the visibility settings are enabled and a checkbox should be shown.
   */
  showVisibilityCheckbox: boolean;
  /**
   * Whether visibility can be changed for this column.
   */
  enableVisibilityCheckbox: boolean;
  /**
   * The current visibility state of the column.
   */
  currentVisibilityState: boolean | 'indeterminate';
  /**
   * Callback when the checkbox is clicked and visibility of the column changes.
   */
  onVisibilityChange: (
    column: Column<TFeatures, TData>,
    currentVisibility: boolean | 'indeterminate',
  ) => void;
  /**
   * Whether the column order settings are enabled and a drag handle should be shown.
   */
  showColumnOrderDragHandle: boolean;
  /**
   * Whether buttons to pin/unpin the column should be shown.
   */
  showColumnPinningMenu: boolean;
  /**
   * The local column pinning state.
   */
  localColumnPinning: ColumnPinningState;
  /**
   * Callback to update the local column pinning state.
   */
  updateLocalColumnPinning: (newState: ColumnPinningState) => void;
  /**
   * The virtual index for grid row positioning.
   */
  virtualIndex: number;
  /**
   * Callback to measure the element for virtualization.
   */
  measureElement: (element: Element | null) => void;
}

/**
 * A single column item in the column settings list.
 */
function InternalDataTableColumnsListItem<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(props: Readonly<DataTableColumnsListItemProps<TFeatures, TData>>) {
  const {
    columnIndex,
    orderedFlatColumns,
    showVisibilityCheckbox,
    enableVisibilityCheckbox,
    currentVisibilityState,
    onVisibilityChange,
    showColumnOrderDragHandle,
    showColumnPinningMenu,
    localColumnPinning,
    updateLocalColumnPinning,
    virtualIndex,
    measureElement,
  } = props;

  const columnName = getColumnLabel(props.column);

  //#region ordering
  const localColumnRef = useRef<HTMLDivElement>(null);
  const { setNodeRef, isDragging } = useSortable({
    id: props.column.key,
    data: {
      column: props.column,
      columnId: props.column.id,
      columnIndex,
      localColumnRef,
    },
  });

  const mergedRefs = useMergeRefs([localColumnRef, setNodeRef, measureElement]);
  //#endregion

  return (
    <Flex
      data-index={virtualIndex}
      data-columnid={props.column.id}
      data-parentcolumnid={props.column.parent?.id}
      data-pinned-position={
        props.column.pinned === false ? undefined : props.column.pinned
      }
      ref={mergedRefs}
      justifyContent="space-between"
      alignItems="center"
      className={clsx('strato-table-settings-item', styles.columnsListItem, {
        [styles.dragging]: isDragging,
      })}
      style={{
        gridRowStart: virtualIndex + 1,
      }}
    >
      {showVisibilityCheckbox ? (
        <ColumnVisibilityCheckbox
          column={props.column}
          enableCheckbox={enableVisibilityCheckbox}
          currentState={currentVisibilityState}
          onVisibilityChange={onVisibilityChange}
        />
      ) : (
        <div className={styles.columnName}>{columnName}</div>
      )}
      <Flex gap={4}>
        {showColumnOrderDragHandle && (
          <ColumnOrderDragHandle
            columnId={props.column.id}
            columnKey={props.column.key}
            isDraggingActive={isDragging}
          />
        )}
        {showColumnPinningMenu && (
          <ColumnPinningMenu
            column={props.column}
            orderedFlatColumns={orderedFlatColumns}
            localColumnPinning={localColumnPinning}
            updateLocalColumnPinning={updateLocalColumnPinning}
          />
        )}
      </Flex>
    </Flex>
  );
}

export const DataTableColumnsListItem = memo(
  InternalDataTableColumnsListItem,
  (prevProps, nextProps) => {
    // Custom props comparison to only re-render when relevant props change.
    // Avoids unnecessary re-renders of all items, e.g. when the orderedFlatColumns prop changes.
    return (
      prevProps.column.key === nextProps.column.key &&
      prevProps.columnIndex === nextProps.columnIndex &&
      prevProps.showVisibilityCheckbox === nextProps.showVisibilityCheckbox &&
      prevProps.enableVisibilityCheckbox ===
        nextProps.enableVisibilityCheckbox &&
      prevProps.currentVisibilityState === nextProps.currentVisibilityState &&
      prevProps.showColumnOrderDragHandle ===
        nextProps.showColumnOrderDragHandle &&
      prevProps.showColumnPinningMenu === nextProps.showColumnPinningMenu &&
      prevProps.localColumnPinning === nextProps.localColumnPinning &&
      prevProps.virtualIndex === nextProps.virtualIndex
    );
  },
);
